// 管理员认证请求工具
// 统一处理所有需要管理员权限的请求，支持 Token 和 Cookie 两种认证方式

import axios from 'axios';

/**
 * 获取管理员请求配置
 * @param {Object} options - 配置选项
 * @param {boolean} options.isAdmin - 是否为管理员（必需）
 * @param {Function} options.getAuthToken - 获取 token 的函数（可选）
 * @param {Object} options.headers - 额外的请求头（可选）
 * @param {Object} options.otherConfig - 其他 axios 配置（可选）
 * @returns {Object} axios 请求配置对象
 * @throws {Error} 如果不是管理员则抛出错误
 */
export const getAdminRequestConfig = ({ isAdmin, getAuthToken, headers = {}, ...otherConfig }) => {
  if (!isAdmin) {
    throw new Error('未登录，请先登录管理员账号');
  }

  const config = {
    ...otherConfig,
    withCredentials: true, // 支持 Cookie 认证
    headers: {
      ...headers
    }
  };

  // 如果有 token，添加到请求头
  if (getAuthToken) {
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return config;
};

/**
 * 执行管理员 GET 请求
 * @param {string} url - 请求 URL
 * @param {Object} options - 配置选项
 * @param {boolean} options.isAdmin - 是否为管理员（必需）
 * @param {Function} options.getAuthToken - 获取 token 的函数（可选）
 * @param {Object} options.params - 查询参数（可选）
 * @param {Object} options.headers - 额外的请求头（可选）
 * @returns {Promise} axios 响应
 */
export const adminGet = async (url, { isAdmin, getAuthToken, params, headers, ...otherConfig } = {}) => {
  const config = getAdminRequestConfig({ isAdmin, getAuthToken, headers, ...otherConfig });
  return axios.get(url, { ...config, params });
};

/**
 * 执行管理员 POST 请求
 * @param {string} url - 请求 URL
 * @param {*} data - 请求数据
 * @param {Object} options - 配置选项
 * @param {boolean} options.isAdmin - 是否为管理员（必需）
 * @param {Function} options.getAuthToken - 获取 token 的函数（可选）
 * @param {Object} options.headers - 额外的请求头（可选）
 * @param {Function} options.onUploadProgress - 上传进度回调（可选）
 * @returns {Promise} axios 响应
 */
export const adminPost = async (url, data, { isAdmin, getAuthToken, headers, onUploadProgress, ...otherConfig } = {}) => {
  const config = getAdminRequestConfig({ isAdmin, getAuthToken, headers, ...otherConfig });
  if (onUploadProgress) {
    config.onUploadProgress = onUploadProgress;
  }
  return axios.post(url, data, config);
};

/**
 * 执行管理员 PUT 请求
 * @param {string} url - 请求 URL
 * @param {*} data - 请求数据
 * @param {Object} options - 配置选项
 * @param {boolean} options.isAdmin - 是否为管理员（必需）
 * @param {Function} options.getAuthToken - 获取 token 的函数（可选）
 * @param {Object} options.headers - 额外的请求头（可选）
 * @returns {Promise} axios 响应
 */
export const adminPut = async (url, data, { isAdmin, getAuthToken, headers, ...otherConfig } = {}) => {
  const config = getAdminRequestConfig({ isAdmin, getAuthToken, headers, ...otherConfig });
  return axios.put(url, data, config);
};

/**
 * 执行管理员 DELETE 请求
 * @param {string} url - 请求 URL
 * @param {Object} options - 配置选项
 * @param {boolean} options.isAdmin - 是否为管理员（必需）
 * @param {Function} options.getAuthToken - 获取 token 的函数（可选）
 * @param {Object} options.headers - 额外的请求头（可选）
 * @returns {Promise} axios 响应
 */
export const adminDelete = async (url, { isAdmin, getAuthToken, headers, ...otherConfig } = {}) => {
  const config = getAdminRequestConfig({ isAdmin, getAuthToken, headers, ...otherConfig });
  return axios.delete(url, config);
};

/**
 * 创建管理员请求的辅助函数（返回一个包含所有方法的对象）
 * 用于在组件中更方便地使用
 * @param {Object} context - AdminContext 的值
 * @param {boolean} context.isAdmin - 是否为管理员
 * @param {Function} context.getAuthToken - 获取 token 的函数
 * @returns {Object} 包含 get, post, put, delete 方法的对象
 */
export const createAdminRequest = ({ isAdmin, getAuthToken }) => {
  return {
    get: (url, config = {}) => adminGet(url, { isAdmin, getAuthToken, ...config }),
    post: (url, data, config = {}) => adminPost(url, data, { isAdmin, getAuthToken, ...config }),
    put: (url, data, config = {}) => adminPut(url, data, { isAdmin, getAuthToken, ...config }),
    delete: (url, config = {}) => adminDelete(url, { isAdmin, getAuthToken, ...config }),
    // 直接获取配置（用于需要自定义 axios 调用的场景）
    getConfig: (config = {}) => getAdminRequestConfig({ isAdmin, getAuthToken, ...config })
  };
};
